<template>
  <div class="music-player" :class="{ expanded: expanded, playing: isPlaying }">
    <!-- 播放完成检测 -->
    <audio
      ref="audio"
      :src="audioSrc"
      preload="auto"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoaded"
      @ended="onEnded"
      @play="isPlaying = true"
      @pause="isPlaying = false"
    ></audio>

    <!-- 展开/收起按钮 -->
    <transition name="fade-scale">
      <div v-show="!expanded" class="mini-trigger" @click="expanded = true">
        <div class="mini-disc" :class="{ spinning: isPlaying }">
          <div class="mini-disc-groove"></div>
          <div class="mini-disc-label">&#9835;</div>
        </div>
        <div class="mini-glow" :class="{ active: isPlaying }"></div>
        <div class="mini-wave" v-if="isPlaying">
          <span></span><span></span><span></span>
        </div>
      </div>
    </transition>

    <!-- 完整播放器 -->
    <transition name="slide-up">
      <div v-show="expanded" class="player-panel" @mouseleave="autoCollapse">
        <!-- 顶部拖拽条 -->
        <div class="panel-handle">
          <span></span>
        </div>

        <!-- 唱片区域 -->
        <div class="disc-section">
          <div class="disc-wrapper" @click="togglePlay">
            <div class="disc-vinyl" :class="{ spinning: isPlaying }">
              <div class="vinyl-grooves" v-for="g in 4" :key="g"></div>
              <div class="vinyl-label">
                <div class="label-hole"></div>
                <div class="label-text">&#9835;</div>
              </div>
            </div>
            <div class="disc-shadow"></div>
          </div>
          <div class="play-overlay" :class="{ hidden: isPlaying }" @click="togglePlay">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </div>
          <div class="pause-overlay" :class="{ hidden: !isPlaying }" @click="togglePlay">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
              <rect x="5" y="3" width="4" height="18" rx="1" />
              <rect x="15" y="3" width="4" height="18" rx="1" />
            </svg>
          </div>
        </div>

        <!-- 歌曲信息 -->
        <div class="song-section">
          <div class="song-title">暖暖</div>
          <div class="song-artist">梁静茹</div>
        </div>

        <!-- 进度条 -->
        <div class="progress-section">
          <span class="time-label">{{ formatTime(currentTime) }}</span>
          <div class="progress-track" ref="progressTrack" @click="seekMusic($event)">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            <div class="progress-thumb" :style="{ left: progress + '%' }"></div>
          </div>
          <span class="time-label">{{ formatTime(duration) }}</span>
        </div>

        <!-- 控制按钮 -->
        <div class="controls-section">
          <div class="ctrl-btn" :class="{ active: isLoop }" @click="isLoop = !isLoop" title="单曲循环">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M17 4l4 4-4 4V9H7v6H5V9a2 2 0 012-2h10V4zM7 20l-4-4 4-4v3h10V9h2v6a2 2 0 01-2 2H7v3z" />
            </svg>
          </div>
          <div class="ctrl-btn main-btn" @click="togglePlay">
            <svg v-if="!isPlaying" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <polygon points="7,3 21,12 7,21" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <rect x="6" y="3" width="4" height="18" rx="1.5" />
              <rect x="14" y="3" width="4" height="18" rx="1.5" />
            </svg>
          </div>
          <div class="ctrl-btn volume-btn" @click="muted = !muted">
            <svg v-if="!muted && volume > 0.3" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.5v7a4.47 4.47 0 002.5-3.5z" />
            </svg>
            <svg v-else-if="!muted && volume > 0" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M18.5 12A4.5 4.5 0 0016 8.5v7c.87-.47 1.73-1.22 2.5-3.5zM3 9v6h4l5 5V4L7 9H3z" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M16.5 12A5 5 0 0014 7.97v2.2l1.98 1.98c.33-.69.52-1.44.52-2.15zM3 9v6h4l5 5V4L7 9H3zm6.5 6.5L3.27 9.27 2 10.54l6.23 6.23L9.5 15.5zM21.27 3L3 21.27 4.27 22.54 22.54 4.27 21.27 3z" />
            </svg>
            <div class="volume-popup">
              <input
                type="range"
                class="volume-slider"
                orient="vertical"
                v-model="volume"
                min="0"
                max="1"
                step="0.01"
                @input="onVolumeChange"
              />
            </div>
          </div>
        </div>

        <!-- 收起按钮 -->
        <div class="collapse-btn" @click="expanded = false">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
          </svg>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MusicPlayer',
  data() {
    return {
      audioSrc: 'https://cdn.fhsj.online/family/%E6%A2%81%E9%9D%99%E8%8C%B9%2B-%2B%E6%9A%96%E6%9A%96.mp3',
      isPlaying: false,
      expanded: false,
      currentTime: 0,
      duration: 0,
      volume: parseFloat(localStorage.getItem('musicVolume') || '0.6'),
      muted: localStorage.getItem('musicMuted') === 'true',
      isLoop: localStorage.getItem('musicLoop') === 'true',
      collapseTimer: null
    }
  },
  computed: {
    progress() {
      if (!this.duration) return 0
      return (this.currentTime / this.duration) * 100
    }
  },
  mounted() {
    this.$nextTick(() => {
      const audio = this.$refs.audio
      if (audio) {
        audio.volume = this.volume
        audio.muted = this.muted
        audio.loop = this.isLoop
        // 尝试自动播放
        this.tryAutoPlay()
      }
    })
  },
  beforeDestroy() {
    if (this.collapseTimer) clearTimeout(this.collapseTimer)
    const audio = this.$refs.audio
    if (audio) {
      audio.pause()
      audio.src = ''
    }
  },
  methods: {
    tryAutoPlay() {
      const audio = this.$refs.audio
      if (!audio) return
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // 浏览器禁止自动播放，显示迷你按钮让用户手动点击
          this.isPlaying = false
        })
      }
    },
    togglePlay() {
      const audio = this.$refs.audio
      if (!audio) return
      if (this.isPlaying) {
        audio.pause()
      } else {
        audio.play().catch(() => {
          this.isPlaying = false
        })
      }
    },
    onTimeUpdate() {
      this.currentTime = this.$refs.audio.currentTime
    },
    onLoaded() {
      this.duration = this.$refs.audio.duration
    },
    onEnded() {
      if (!this.isLoop) {
        this.isPlaying = false
      }
    },
    seekMusic(e) {
      const track = this.$refs.progressTrack
      const audio = this.$refs.audio
      if (!track || !audio) return
      const rect = track.getBoundingClientRect()
      const ratio = (e.clientX - rect.left) / rect.width
      audio.currentTime = ratio * this.duration
    },
    onVolumeChange() {
      const audio = this.$refs.audio
      if (!audio) return
      audio.volume = this.volume
      audio.muted = false
      this.muted = false
      localStorage.setItem('musicVolume', this.volume)
    },
    toggleMute() {
      this.muted = !this.muted
    },
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    autoCollapse() {
      if (this.collapseTimer) clearTimeout(this.collapseTimer)
      this.collapseTimer = setTimeout(() => {
        this.expanded = false
      }, 4000)
    }
  },
  watch: {
    volume(val) {
      localStorage.setItem('musicVolume', val)
    },
    muted(val) {
      const audio = this.$refs.audio
      if (audio) audio.muted = val
      localStorage.setItem('musicMuted', val)
    },
    isLoop(val) {
      const audio = this.$refs.audio
      if (audio) audio.loop = val
      localStorage.setItem('musicLoop', val)
    }
  }
}
</script>

<style scoped>
/* ========== 容器 ========== */
.music-player {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', sans-serif;
}

/* ========== 迷你模式 ========== */
.mini-trigger {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.mini-trigger:hover {
  border-color: rgba(0, 210, 255, 0.5);
  box-shadow:
    0 8px 40px rgba(0, 210, 255, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transform: scale(1.08);
}

.mini-glow {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid transparent;
  transition: all 0.5s ease;
}

.mini-glow.active {
  border-color: rgba(0, 210, 255, 0.5);
  animation: glowPulse 2s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% { box-shadow: 0 0 8px rgba(0, 210, 255, 0.2); }
  50% { box-shadow: 0 0 20px rgba(0, 210, 255, 0.45); }
}

.mini-disc {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.mini-disc.spinning {
  animation: discSpin 3s linear infinite;
}

@keyframes discSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.mini-disc-groove {
  position: absolute;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.mini-disc-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  z-index: 1;
}

.mini-wave {
  position: absolute;
  bottom: -16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 12px;
}

.mini-wave span {
  display: block;
  width: 3px;
  background: linear-gradient(to top, #00D2FF, #FA709A);
  border-radius: 1.5px;
  animation: wave 0.8s ease-in-out infinite;
}

.mini-wave span:nth-child(1) { height: 6px; animation-delay: 0s; }
.mini-wave span:nth-child(2) { height: 11px; animation-delay: 0.15s; }
.mini-wave span:nth-child(3) { height: 8px; animation-delay: 0.3s; }

@keyframes wave {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.4); }
}

/* ========== 展开面板 ========== */
.player-panel {
  width: 300px;
  background: rgba(15, 15, 35, 0.88);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 20px 24px 18px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(0, 210, 255, 0.08);
  position: relative;
  overflow: hidden;
}

/* 流光边框效果 */
.player-panel::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border-radius: 20px;
  padding: 1.5px;
  background: linear-gradient(135deg, rgba(0, 210, 255, 0.4), transparent 30%, transparent 70%, rgba(250, 112, 154, 0.4));
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

/* 内部微光 */
.player-panel::after {
  content: '';
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  width: 180px;
  height: 60px;
  background: radial-gradient(ellipse, rgba(0, 210, 255, 0.12), transparent);
  pointer-events: none;
}

.panel-handle {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.panel-handle span {
  display: block;
  width: 32px;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.2);
}

/* ========== 唱片 ========== */
.disc-section {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
}

.disc-wrapper {
  position: relative;
  cursor: pointer;
}

.disc-vinyl {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%);
  position: relative;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.5),
    0 0 0 3px rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease;
}

.disc-vinyl.spinning {
  animation: discSpin 3s linear infinite;
}

.vinyl-grooves {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.06);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.vinyl-grooves:nth-child(1) { width: 80px; height: 80px; }
.vinyl-grooves:nth-child(2) { width: 64px; height: 64px; }
.vinyl-grooves:nth-child(3) { width: 48px; height: 48px; }
.vinyl-grooves:nth-child(4) { width: 36px; height: 36px; }

.vinyl-label {
  position: absolute;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF6B9D, #C44D7A);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.label-hole {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0a0a14;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.label-text {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.8);
  z-index: 1;
}

.disc-shadow {
  position: absolute;
  bottom: -6px;
  left: 10px;
  width: 80px;
  height: 12px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  filter: blur(6px);
}

.play-overlay,
.pause-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;
  z-index: 5;
}

.play-overlay:hover,
.pause-overlay:hover {
  background: rgba(0, 210, 255, 0.2);
  border-color: rgba(0, 210, 255, 0.5);
}

.hidden {
  opacity: 0;
  pointer-events: none;
}

/* ========== 歌曲信息 ========== */
.song-section {
  text-align: center;
  margin-bottom: 14px;
}

.song-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #00D2FF, #4FACFE, #FA709A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.song-artist {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 1px;
}

/* ========== 进度条 ========== */
.progress-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.time-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  font-variant-numeric: tabular-nums;
  min-width: 32px;
}

.progress-track {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: visible;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00D2FF, #4FACFE, #FA709A);
  border-radius: 2px;
  position: relative;
  transition: width 0.1s linear;
}

.progress-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 8px rgba(0, 210, 255, 0.6);
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.progress-track:hover .progress-thumb {
  opacity: 1;
}

/* ========== 控制按钮 ========== */
.controls-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.ctrl-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.55);
  transition: all 0.25s ease;
  position: relative;
}

.ctrl-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.ctrl-btn.active {
  color: #00D2FF;
}

.ctrl-btn.main-btn {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, rgba(0, 210, 255, 0.2), rgba(79, 172, 254, 0.15));
  border: 1.5px solid rgba(0, 210, 255, 0.3);
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 210, 255, 0.2);
}

.ctrl-btn.main-btn:hover {
  border-color: rgba(0, 210, 255, 0.6);
  box-shadow: 0 4px 24px rgba(0, 210, 255, 0.35);
}

/* 音量弹窗 */
.volume-btn {
  position: relative;
}

.volume-popup {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 15, 35, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 10px 6px;
  margin-bottom: 8px;
  height: 100px;
  display: flex;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.volume-btn:hover .volume-popup {
  opacity: 1;
  pointer-events: auto;
}

.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  outline: none;
  transform: rotate(-90deg);
  transform-origin: center center;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 0 8px rgba(0, 210, 255, 0.5);
}

.volume-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  border: none;
  box-shadow: 0 0 8px rgba(0, 210, 255, 0.5);
}

/* ========== 收起按钮 ========== */
.collapse-btn {
  position: absolute;
  top: 12px;
  right: 14px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.25s ease;
}

.collapse-btn:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.06);
}

/* ========== 过渡动画 ========== */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-scale-enter,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.slide-up-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-up-leave-active {
  transition: all 0.3s ease-in;
}

.slide-up-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.92);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.96);
}

/* ========== 响应式 ========== */
@media (max-width: 480px) {
  .music-player {
    bottom: 16px;
    right: 16px;
  }

  .player-panel {
    width: 280px;
    padding: 16px 18px 14px;
  }

  .disc-vinyl {
    width: 80px;
    height: 80px;
  }
}
</style>
